import {
  AnswerHtml,
  Chapter,
  PdfHtml,
  Question,
  QuestionHtml,
  QuestionNoMath,
  QuestionObjectiveNoMath,
  QuestionSubjective,
} from './../../resource/question/question.model';
import { SubjectiveContent } from '@/resource/question/question.type';
import { QuestionLanguage, Subject } from '@/resource/subject/subject.model';
import { axios } from '@/utils/axios';
import { JSONContent } from '@tiptap/core';
import { SaveQuestionEvent } from '@/resource/question/question.store';
import { CollectionItem } from '@/resource/collection-item/collection-item.model';

interface BuildJsonObjective {
  picture: string;
  question: string;
  correctAnswers: string[];
  wrongAnswers: string[];
}

type BuildJsonSubjective = SubjectiveContent;

type BuildJson = BuildJsonObjective | BuildJsonSubjective;

export interface BuildSubjectiveQuestion {
  id: number | null;
  chapterId: number;
  languages: QuestionLanguage[];
  keywords: string;
  difficulty: number;
  editorVersion: string | null;
  //
  questionJson: JSONContent | null;
  questionHtmls: QuestionHtml[] | null;
  pdfHtmls: PdfHtml[] | null;
}

export interface BuildObjectiveQuestion {
  id: number | null;
  chapterId: number;
  languages: QuestionLanguage[];
  keywords: string;
  difficulty: number;
  editorVersion: string | null;
  //
  questionJson: JSONContent | null;
  answerCorrectJson: JSONContent | null;
  answerWrongJsons: JSONContent[] | null;
  questionHtmls: QuestionHtml[] | null;
  answerCorrectHtmls: AnswerHtml[] | null;
  answerWrongHtmls: AnswerHtml[][] | null;
}

export const buildSubjectiveQuestionEmpty: BuildSubjectiveQuestion = {
  id: null,
  chapterId: 1,
  languages: [],
  keywords: '',
  difficulty: 1,
  editorVersion: '',
  //
  questionJson: null,
  questionHtmls: [],
  pdfHtmls: [],
};

export const buildObjectiveQuestionEmpty: BuildObjectiveQuestion = {
  id: null,
  chapterId: 1,
  languages: [],
  keywords: '',
  difficulty: 1,
  editorVersion: '',
  //
  questionJson: null,
  answerCorrectJson: null,
  answerWrongJsons: [],
  questionHtmls: [],
  answerCorrectHtmls: [],
  answerWrongHtmls: [],
};
export interface BuildQuestion {
  buildChapter: Chapter;
  buildJson: BuildJson | null;
  questionJson: JSONContent | null;
  questionHtmls: QuestionHtml[] | null;
  pdfHtmls: PdfHtml[] | null;
  languages: QuestionLanguage[];
  keywords: string;
  difficulty: number;
  parentId: number | null;
  editorVersion: string | null;
}

export function extractBuildQuestion(
  question: QuestionNoMath,
  isSubjective: boolean,
): BuildQuestion {
  let buildJson: BuildJson | null;

  if (isSubjective) {
    const questionSubjective = question as QuestionSubjective;
    buildJson = questionSubjective.question;
  } else {
    const questionObjective = question as QuestionObjectiveNoMath;

    buildJson = {
      picture:
        questionObjective.pictures && questionObjective.pictures.length
          ? questionObjective.pictures[0]
          : '',
      question: questionObjective.question,
      correctAnswers: questionObjective.correctAnswers,
      wrongAnswers: questionObjective.wrongAnswers,
    };
  }

  const buildQuestion: BuildQuestion = {
    buildChapter: question.chapter,
    questionJson: (question as QuestionSubjective).questionJson || null,
    questionHtmls: (question as QuestionSubjective).questionHtmls || null,
    pdfHtmls: (question as QuestionSubjective).pdfHtmls || null,
    languages: question.languages,
    keywords: question.keywords.join(', '),
    difficulty: question.difficulty,
    buildJson,
    parentId: question.parentId,
    editorVersion: null,
  };

  return buildQuestion;
}

export interface SubjectDetails {
  uuid: string;
  name: string;
  totalDraft: string;
  totalPublish: string;
  totalPublic: string;
  totalPrivate: string;
  totalChapters: number;
}

export async function getAllSubjects(): Promise<Subject[]> {
  const { data } = await axios.get('subjects/raw');
  return data;
}

export async function getAllSubjectsDetails(): Promise<SubjectDetails[]> {
  const { data } = await axios.get('subjects');
  return data;
}

export async function getUsersCount(): Promise<number> {
  const { data } = await axios.get('users/count');
  return data;
}

export async function deleteQuestions(params: {
  questionIds: number[];
  isSubjective: boolean;
}) {
  return await axios.delete('questions', { data: params });
}

export async function publishQuestions(params: {
  questionIds: number[];
  isSubjective: boolean;
}) {
  return await axios.put('questions/publish', params);
}

export async function unpublishQuestions(params: {
  questionIds: number[];
  isSubjective: boolean;
}) {
  return await axios.put('questions/unpublish', params);
}

interface GeneratePDFParams {
  questionId: number;
  isSubjective: boolean;
  buildQuestion: BuildQuestion;
}

export async function generatePDF(params: GeneratePDFParams): Promise<any> {
  const { data } = await axios.post(
    'questions/pdf',
    { ...params },
    { responseType: 'arraybuffer' },
  );
  return data;
}

export interface SaveQuestionParams {
  event: SaveQuestionEvent;
  questionId: number | null;
  isSubjective: boolean;
  buildQuestion: BuildSubjectiveQuestion | BuildObjectiveQuestion;
}

export async function saveQuestion(
  params: SaveQuestionParams,
): Promise<Question | null> {
  let question: Question | null = null;
  try {
    const { data } = await axios.post('questions/save', params);

    question = data.question;
  } catch (error) {
    question = null;
  }

  return question;
}

export async function getQuestionById(
  questionId: number,
  isSubjective: boolean,
): Promise<Question | null> {
  let question: Question | null = null;
  try {
    const { data } = await axios.get(`questions/${questionId}`, {
      params: {
        is_subjective: isSubjective,
      },
    });

    question = data.question;
  } catch (error) {
    question = null;
  }

  return question;
}

export interface AddQuestionByUserParams {
  questionId: number | null;
  isSubjective: boolean;
  buildQuestion: BuildSubjectiveQuestion | BuildObjectiveQuestion;
}

export interface UpdateQuestionByUserParams extends AddQuestionByUserParams {
  collectionItemId: number;
}

export async function addQuestionByUser(
  params: AddQuestionByUserParams,
): Promise<Question | null> {
  let question: Question | null = null;
  try {
    const { data } = await axios.post('questions/add-question', {
      event: SaveQuestionEvent.CreateDaft,
      ...params,
    });

    question = data.question;
  } catch (error) {
    question = null;
  }

  return question;
}

export async function updateQuestionByUser(
  params: UpdateQuestionByUserParams,
): Promise<Question | null> {
  let question: Question | null = null;
  try {
    const { data } = await axios.post('questions/update-question', {
      event: SaveQuestionEvent.Update,
      ...params,
    });

    question = data.question;
  } catch (error) {
    question = null;
  }

  return question;
}

export interface AddQuestionToPaperParams {
  questionId: number;
  collectionUuid: string;
  answerPosition: number;
  isSubjective: boolean;
  languages: QuestionLanguage[];
}

export async function addQuestionToPaper(
  params: AddQuestionToPaperParams,
): Promise<CollectionItem | null> {
  let collectionItem: CollectionItem | null = null;

  try {
    const { data } = await axios.post('collection-items', params);

    collectionItem = data;
  } catch (error) {
    collectionItem = null;
  }

  return collectionItem;
}

export interface SubjectQuestions {
  subject: Subject;
  forms: Form2[];
}

interface Form2 {
  form: number;
  totalQuestion: number;
  totalDraftQuestion: number;
  totalPublishQuestion: number;
  objective: TotalQuestions;
  subjective: TotalQuestions;
}

interface TotalQuestions {
  totalQuestion: number;
  totalDraftQuestion: number;
  totalPublishQuestion: number;
}

export async function getTotalQuestionsBySubject(
  subjectUuid: string,
): Promise<SubjectQuestions | null> {
  let subjectQuestions: SubjectQuestions | null = null;
  try {
    const { data } = await axios.get(
      `questions/total-questions/${subjectUuid}`,
    );

    subjectQuestions = data;
  } catch (error) {
    subjectQuestions = null;
  }

  return subjectQuestions;
}
